import { useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../components/Modal';
import type { Product, ProductImage } from '../../../../types';
import ProductImageThumbnail from './ProductImageThumbnail';
import * as s from './ProductImagesViewerModal.styles';

interface ProductImagesViewerModalProps {
  product: Product;
  closeModal: () => void;
  images?: ProductImage[];
  initialIndex?: number;
}

const imagesForProduct = (product: Product): ProductImage[] => (
  product.images?.length ? product.images : product.image ? [product.image] : []
);

const ProductImagesViewerModal = ({
  product,
  closeModal,
  images: providedImages,
  initialIndex = 0,
}: ProductImagesViewerModalProps) => {
  const { t } = useTranslation();
  const images = useMemo(
    () => providedImages ?? imagesForProduct(product),
    [product, providedImages],
  );
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  useEffect(() => {
    setSelectedIndex(Math.min(Math.max(initialIndex, 0), Math.max(images.length - 1, 0)));
  }, [images.length, initialIndex, product.CodigoProduto]);

  const selectImage = (index: number) => {
    if (images.length === 0) return;
    setSelectedIndex((index + images.length) % images.length);
  };

  const selectedImage = images[selectedIndex] ?? null;

  return (
    <Modal
      title={`${t('viewProductImages')}: ${product.Descricao}`}
      showModal
      closeModal={closeModal}
      onlyCloseButton
      maximized={false}
      style={{ maxWidth: '900px', width: 'calc(100vw - 2rem)' }}
      bodyStyle={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
    >
      <s.GalleryContent>
        <s.GalleryStage>
          {images.length > 1 && (
            <s.GalleryArrow
              type="button"
              aria-label={t('previousProductImage')}
              onClick={() => selectImage(selectedIndex - 1)}
            >
              <FiChevronLeft size={22} aria-hidden="true" />
            </s.GalleryArrow>
          )}
          <s.PreviewImage>
            <ProductImageThumbnail image={selectedImage} alt={product.Descricao} size="preview" />
          </s.PreviewImage>
          {images.length > 1 && (
            <s.GalleryArrow
              type="button"
              aria-label={t('nextProductImage')}
              onClick={() => selectImage(selectedIndex + 1)}
            >
              <FiChevronRight size={22} aria-hidden="true" />
            </s.GalleryArrow>
          )}
        </s.GalleryStage>

        <s.ImagePosition aria-live="polite">
          {images.length > 0
            ? t('productImagePosition', { current: selectedIndex + 1, total: images.length })
            : t('noProductImage')}
        </s.ImagePosition>

        {images.length > 1 && (
          <s.ThumbnailStrip>
            {images.map((image, index) => (
              <s.ThumbnailButton
                key={image.id}
                type="button"
                $selected={selectedIndex === index}
                aria-label={t('selectProductImage', { current: index + 1 })}
                aria-current={selectedIndex === index ? 'true' : undefined}
                onClick={() => selectImage(index)}
              >
                <ProductImageThumbnail image={image} alt={`${product.Descricao} ${index + 1}`} />
              </s.ThumbnailButton>
            ))}
          </s.ThumbnailStrip>
        )}
      </s.GalleryContent>
    </Modal>
  );
};

export default ProductImagesViewerModal;
